import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { format, compareAsc } from 'date-fns';
import {
    useTheme,
    useMediaQuery,
    Box,
    ToggleButton,
    Tooltip,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import {
    UnfoldMore,
    UnfoldLess,
    ViewTimeline,
    AutoFixHigh,
} from "@mui/icons-material";
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT, EXCLUDED_FLIGHT_STATUS } from 'state/constants';
import { removeItemByValueImmutable, convertLocalTimeToUTC } from 'state/helpers';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import FilterButton from 'components/FilterButton';
import DateTimePicker from "components/DateTimePicker";
import ExpandableSearch from "components/ExpandableSearch";
import FlightFilterDialog from 'components/FlightFilterDialog';
import OptimizeScheduleDialog from './OptimizeScheduleDialog';
import FlightGanttChart from './dhtmlx';
import GanttRoot from './GanttRoot';


const usePressAndHold = (action) => {
    const timerRef = useRef(null);

    const stop = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    const start = useCallback(() => {
        action();
        let delay = 150;
        const schedule = () => {
            timerRef.current = setTimeout(() => {
                action();
                delay = Math.max(30, Math.floor(delay * 0.8));
                schedule();
            }, delay);
        };
        timerRef.current = setTimeout(schedule, 400);
    }, [action]);

    return { 
        onMouseDown: start, 
        onMouseUp: stop, 
        onMouseLeave: stop, 
        onTouchStart: start, 
        onTouchEnd: stop 
    };
};

const GanttChartPage = () => {
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [isOptimizeDialogOpen, setIsOptimizeDialogOpen] = useState(false);
    // previewData: null | { assignments: Map<id,reg>, changes: [], stats: {} }
    const [previewData, setPreviewData] = useState(null);
    const [filters, setFilters] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const [isShownCrewRoutes, setIsShownCrewRoutes] = useState(true);
    const [switched, setSwitched] = useState(true);

    const [rowHeight, setRowHeight] = useState(50);
    const [cellWidth, setCellWidth] = useState(20);

    const flightsData = useSelector((state) => state.flights);

    const handleOptimizePreview = useCallback((result) => {
        setPreviewData(result);
    }, []);

    const flights = useMemo(() => {
        if (!Array.isArray(flightsData)) return [];
        
        return removeItemByValueImmutable(
            flightsData, 
            'flightStatus', 
            EXCLUDED_FLIGHT_STATUS
        );
    }, [flightsData]);

    const [selectedTime, setSelectedTime] = useState(new Date());

    const ports = useMemo(() => Array.from(new Set(
        flights.flatMap(flight => [flight.dep, flight.arr])
    )).filter(Boolean).sort(), [flights]);

    const [port, setPort] = useState('');

    const convertedSelectedTime = useMemo(() => convertLocalTimeToUTC(selectedTime), [selectedTime]);

    const filteredFlights = useMemo(() => {
        // 1. Apply filters
        const filtered = !filters ? flights : flights.filter((flight) => {
            const flightEtdStr = timeMode === 'UTC' ? flight.etd?.substring(0, 5) : flight.etdLocalBase?.substring(0, 5);
            const { minEtd, maxEtd } = filters;

            if (minEtd && (!flightEtdStr || flightEtdStr < minEtd)) return false;
            if (maxEtd && (!flightEtdStr || flightEtdStr > maxEtd)) return false;

            return Object.entries(filters).every(([key, value]) => {
                if (!value || key === 'minEtd' || key === 'maxEtd') return true;

                if (key === 'flightDate') {
                    const formattedDate = flight[key] ? format(new Date(flight[key]), 'yyyy-MM-dd') : null;
                    return formattedDate === format(new Date(value), 'yyyy-MM-dd');
                }

                if (key === 'flightNo') return flight[key] === Number(value);

                if (key === 'sector') return flight[key]?.toLowerCase().includes(value.toLowerCase());

                if (key === 'acType' || key === 'flightReg') {
                    return Array.isArray(value) ? value.includes(flight[key]) : flight[key] === value;
                }

                return flight[key] === value;
            });
        });

        // 2. Apply port + time matching
        if (!port || !convertedSelectedTime) return filtered;

        const flightsByReg = filtered.reduce((acc, flight) => {
            const reg = flight.flightReg;
            if (!reg) return acc;
            if (!acc[reg]) acc[reg] = [];
            acc[reg].push(flight);
            return acc;
        }, {});

        const result = [];

        for (const [, regFlights] of Object.entries(flightsByReg)) {
            const sortedFlights = [...regFlights].sort((a, b) =>
                new Date(a.atd || a.etd) - new Date(b.atd || b.etd)
            );

            for (let i = 0; i < sortedFlights.length - 1; i++) {
                const cur = sortedFlights[i];
                const nxt = sortedFlights[i + 1];

                const isLocalBase = timeMode === 'LocalBase';
                const arrTime = new Date(isLocalBase ? (cur.fullAtaLocalBase || cur.fullEtaLocalBase) : (cur.fullAta || cur.fullEta));
                const depTime = new Date(isLocalBase ? (nxt.fullAtdLocalBase || nxt.fullEtdLocalBase) : (nxt.fullAtd || nxt.fullEtd));

                if (cur.arr === port && compareAsc(arrTime, convertedSelectedTime) < 0 &&
                    nxt.dep === port && compareAsc(depTime, convertedSelectedTime) > 0) {
                    result.push(...regFlights);
                    break;
                }
            }
        }

        return result;
    }, [flights, filters, port, convertedSelectedTime, timeMode]);

    const searchedFlights = useMemo(() => {
        if (!searchQuery) return filteredFlights;
        const q = searchQuery.toLowerCase();
        return filteredFlights.filter(f =>
            `${f.flightNo} ${f.sector} ${f.sectorType}`.toLowerCase().includes(q)
        );
    }, [filteredFlights, searchQuery]);

    const { 
        acTypeOptions, 
        flightRegOptions, 
        sectorTypeOptions, 
        departureTypeOptions, 
        arrivalTypeOptions 
    } = useMemo(() => {
        const acTypes = new Set(), flightRegs = new Set(), sectorTypes = new Set(), departureTypes = new Set(), arrivalTypes = new Set();
        
        flights?.forEach(f => {
            if (f.acType) acTypes.add(f.acType);
            if (f.flightReg) flightRegs.add(f.flightReg);
            if (f.sectorType) sectorTypes.add(f.sectorType);
            if (f.departureType) departureTypes.add(f.departureType);
            if (f.arrivalType) arrivalTypes.add(f.arrivalType);
        });

        return {
            acTypeOptions: [...acTypes].sort(),
            flightRegOptions: [...flightRegs].sort(),
            sectorTypeOptions: [...sectorTypes].sort(),
            departureTypeOptions: [...departureTypes].sort(),
            arrivalTypeOptions: [...arrivalTypes].sort(),
        };
    }, [flights]);

    const handleFilterOpen = useCallback(() => setIsFilterDialogOpen(true), []);
    const handleFilterClose = useCallback(() => setIsFilterDialogOpen(false), []);
    const handleFilterApply = useCallback((filterValues) => setFilters(filterValues), []);
    const toggleCrewRoutes = useCallback(() => setIsShownCrewRoutes(prev => !prev), []);
    const toggleSwitched = useCallback(() => setSwitched(prev => !prev), []);
    const decreaseRowHeight = useCallback(() => setRowHeight(prev => Math.max(1, prev - 1)), []);
    const increaseRowHeight = useCallback(() => setRowHeight(prev => prev + 1), []);
    const decreaseCellWidth = useCallback(() => setCellWidth(prev => Math.max(1, prev - 1)), []);
    const increaseCellWidth = useCallback(() => setCellWidth(prev => prev + 1), []);
    const holdDecreaseRowHeight = usePressAndHold(decreaseRowHeight);
    const holdIncreaseRowHeight = usePressAndHold(increaseRowHeight);
    const holdDecreaseCellWidth = usePressAndHold(decreaseCellWidth);
    const holdIncreaseCellWidth = usePressAndHold(increaseCellWidth);

    return (
        <Box className="main-container-box">
            <FlexBetween>
                <Header title="Flights" subtitle="Gantt Chart" />
                <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Search" arrow placement="top">
                        <span>
                            <ExpandableSearch
                                value={searchQuery}
                                onChange={setSearchQuery}
                                expandedWidth={120}
                            />
                        </span>
                    </Tooltip>
                    {!isMobile && (
                        <FlexBetween gap={1}>                            
                            <FormControlLabel
                                label="Crew Routes"
                                control={
                                    <Switch
                                        checked={isShownCrewRoutes}
                                        onChange={toggleCrewRoutes}
                                    />
                                }
                                sx={{ m: 0 }}
                            />

                            {port && (
                                <Tooltip title="Choose time when aircraft locate" arrow placement="top">
                                    <span>
                                        <DateTimePicker
                                            selectedTime={selectedTime}
                                            setSelectedTime={setSelectedTime}
                                        />
                                    </span>
                                </Tooltip>
                            )}
                            <Tooltip title="Choose port where aircraft locate" arrow placement='top'>
                                <span>
                                    <FormControl size="small" sx={{ minWidth: 75 }}>
                                        <InputLabel>Port</InputLabel>
                                        <Select
                                            label="Port"
                                            name="port"
                                            value={port}
                                            onChange={(e) => setPort(e.target.value)}
                                        >
                                            <MenuItem value=''>None</MenuItem>
                                            {ports.map((portCode) => (
                                                <MenuItem key={portCode} value={portCode}>
                                                    {portCode}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </span>
                            </Tooltip>
                            <StyledButton {...holdDecreaseRowHeight}><UnfoldLess /></StyledButton>
                            <StyledButton {...holdIncreaseRowHeight}><UnfoldMore /></StyledButton>
                            <StyledButton {...holdDecreaseCellWidth} sx={{ transform: 'rotate(90deg)' }}>
                                <UnfoldLess />
                            </StyledButton>
                            <StyledButton {...holdIncreaseCellWidth} sx={{ transform: 'rotate(90deg)' }}>
                                <UnfoldMore />
                            </StyledButton>
                        </FlexBetween>
                    )}                    
                    <Tooltip title="Optimize Aircraft Tail" arrow>
                        <span>
                            <StyledButton onClick={() => setIsOptimizeDialogOpen(true)}>
                                <AutoFixHigh />
                            </StyledButton>
                        </span>
                    </Tooltip>
                    <FilterButton 
                        filters={filters} 
                        onClick={handleFilterOpen} 
                    />
                    <ToggleButton
                        size="small"
                        value="gantt"
                        selected={switched}
                        onChange={toggleSwitched}
                    >
                        <ViewTimeline />
                    </ToggleButton>
                </Box>
            </FlexBetween>
            
            <Box
                display="grid"
                gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                gap={2}
            >
                <Box
                    height={LAYOUT_HEIGHT}
                    sx={{ gridColumn: "span 12", minHeight: 0 }}
                >
                    {switched ? (
                        <FlightGanttChart
                            rowHeight={rowHeight}
                            cellWidth={cellWidth}
                            isShownCrewRoutes={isShownCrewRoutes}
                            searchQuery={searchQuery}
                            flights={searchedFlights}
                        />
                    ) : (
                        <GanttRoot
                            rowHeight={rowHeight}
                            cellWidth={cellWidth}
                            isShownCrewRoutes={isShownCrewRoutes}
                            flights={searchedFlights}
                            overrideAssignments={previewData?.assignments ?? null}
                        />
                    )}
                </Box>
            </Box>

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions}
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            {isOptimizeDialogOpen &&
                <OptimizeScheduleDialog
                    open={isOptimizeDialogOpen}
                    onClose={() => setIsOptimizeDialogOpen(false)}
                    flights={flights}
                    onPreview={handleOptimizePreview}
                />
            }
        </Box>
    );
};

export default GanttChartPage;
